import { useMemo } from "react";
import type { ProjectOwnerWithUser, BudgetLineRow, TimeEntry } from "@/hooks/use-project-detail";
import type { UserLookup } from "@/hooks/use-users";
import { useSalesUnits, buildSalesUnitMap } from "@/hooks/use-sales-units";
import { ProjectTeamOverview, buildTeamOverview } from "@/components/project/ProjectTeamOverview";
import { EditableProjectUserField } from "@/components/project/EditableProjectUserField";
import { SrIcon } from "@/components/ui/SrIcon";
import { Avatar } from "@/components/shared/Avatar";
import { CollapsibleCard } from "@/components/shared/CollapsibleCard";
import { useProjectPermission } from "@/hooks/use-project-permission";
import { useFreelancerProjectsEnabled } from "@/hooks/use-freelancer-projects";
import { ProjectFreelancersSection } from "@/components/freelancers/ProjectFreelancersSection";

interface TeamWidgetProps {
  projectId: string;
  /** VIB-757: project.updated_at brukes til optimistic locking på rolle-endringer. */
  projectUpdatedAt: string;
  owners: ProjectOwnerWithUser[];
  productionLeadId: string | null;
  advisorId: string | null;
  allUsers: UserLookup[];
  budgetLines: BudgetLineRow[];
  timeEntries: TimeEntry[];
  userMap: Map<string, UserLookup>;
}

export function TeamWidget({
  projectId,
  projectUpdatedAt,
  owners,
  productionLeadId,
  advisorId,
  allUsers,
  budgetLines,
  timeEntries,
  userMap,
}: TeamWidgetProps) {
  const { data: salesUnits } = useSalesUnits();
  const salesUnitMap = buildSalesUnitMap(salesUnits ?? []);
  // VIB-722c: manage_team gates redigering av advisor/PL og legg-til-eier.
  // Admin/superadmin overstyrer; default = project_lead + client_advisor=true.
  const canManageTeam = useProjectPermission(projectId, "manage_team");
  const freelancersOn = useFreelancerProjectsEnabled();

  // Teammedlemmer = alle som har minst én tildelt oppgave (også
  // rolleinnehavere som PL/kunderådgiver/selger hvis de jobber på prosjektet).
  // Rene rolleinnehavere uten oppgaver vises kun øverst i seksjonen.
  const taskMembers = useMemo(
    () =>
      buildTeamOverview({
        budgetLines,
        timeEntries,
        userMap,
        productionLeadId,
        owners,
        advisorId,
        allUsers,
      }).filter((m) => m.counts.total > 0),
    [budgetLines, timeEntries, userMap, productionLeadId, owners, advisorId, allUsers],
  );

  // Tell unike personer på toppen + teammedlemmer for header-badge.
  const headerCount = (() => {
    const ids = new Set<string>();
    if (advisorId) ids.add(advisorId);
    if (productionLeadId) ids.add(productionLeadId);
    for (const o of owners) ids.add(o.user_id);
    for (const m of taskMembers) ids.add(m.userId);
    return ids.size;
  })();

  return (
    <CollapsibleCard
      title="Team & eierskap"
      icon={<SrIcon name="users" className="w-5 h-5 text-primary" />}
      count={headerCount}
      contentClassName="border-t p-4 space-y-3"
    >
          {/* Kunderådgiver (projects.advisor_id) */}
          {(() => {
            const advisor = advisorId ? allUsers.find((u) => u.id === advisorId) : null;
            return (
              <div className="flex items-center gap-3 group">
                <Avatar name={advisor?.full_name ?? "?"} avatarUrl={advisor?.avatar_url} size="md" className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <EditableProjectUserField
                    projectId={projectId}
                    projectUpdatedAt={projectUpdatedAt}
                    column="advisor_id"
                    currentUserId={advisorId}
                    displayName={advisor?.full_name || "—"}
                    allUsers={allUsers}
                    canEdit={canManageTeam}
                  />
                </div>
                <span className="text-xs text-muted-foreground">Kunderådgiver</span>
              </div>
            );
          })()}

          {/* Prosjektleder (projects.production_lead_id) */}
          {(() => {
            const plUser = productionLeadId
              ? allUsers.find((u) => u.id === productionLeadId)
              : null;
            return (
              <div className="flex items-center gap-3 group">
                <Avatar name={plUser?.full_name ?? "?"} avatarUrl={plUser?.avatar_url} size="md" className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <EditableProjectUserField
                    projectId={projectId}
                    projectUpdatedAt={projectUpdatedAt}
                    column="production_lead_id"
                    currentUserId={productionLeadId}
                    displayName={plUser?.full_name || "—"}
                    allUsers={allUsers}
                    canEdit={canManageTeam}
                  />
                </div>
                <span className="text-xs text-muted-foreground">Prosjektleder</span>
              </div>
            );
          })()}

          {/* Owners with commission */}
          {owners.length > 0 && (
            <>
              <div className="border-t pt-3" />
              {owners.map(owner => {
                const ownerUser = owner.user;
                return (
                  <div key={owner.user_id} className="flex items-center gap-3">
                    <Avatar name={ownerUser?.full_name ?? "?"} avatarUrl={ownerUser?.avatar_url} size="md" className="shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{ownerUser?.full_name || "—"}</div>
                    </div>
                    <span className="text-xs text-muted-foreground">Selger</span>
                  </div>
                );
              })}
            </>
          )}

          {/* Teammedlemmer — alle med oppgaver (også rolleinnehavere) */}
          {taskMembers.length > 0 && (
            <div className="border-t pt-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                Teammedlemmer
              </h4>
              <ProjectTeamOverview
                budgetLines={budgetLines}
                timeEntries={timeEntries}
                userMap={userMap}
                productionLeadId={productionLeadId}
                owners={owners}
                advisorId={advisorId}
                allUsers={allUsers}
              />
            </div>
          )}

          {/* VIB-2927: frilansere fra eksterne linjer og koblede leverandører */}
          {freelancersOn && <ProjectFreelancersSection projectId={projectId} />}
    </CollapsibleCard>
  );
}
